// The thread UI: a tabbed view (chat + post-search results). The sole thread
// implementation — ChatView renders it directly.

import {Box, Stack} from "@mantine/core";
import UserMessage from "./UserMessage";
import {PendingPasteAttachment} from "../utils/pasteAttachments";
import AssistantMessage from "./AssistantMessage";
import {FollowUpSuggestionChips} from "./FollowUpSuggestionChips";
import {ExperimentalEngineIndicator} from "../components/ExperimentalEngineIndicator";
import {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {useSearchParams} from "react-router-dom";
import classes from "../components/scrollContainer.module.css";
import threadClasses from "./Thread.module.css";
import {useAppSelector} from "../../../app/hooks";
import {bffApi} from "../bffApi";
import {ChatInput} from "./ChatInput";
import {useChatScroll} from "../hooks/useChatScroll";
import {useGutterHover} from "../components/useGutterHover";
import {useThreadArtifacts} from "../hooks/useThreadArtifacts";
import {useMessageHeightTracking} from "../hooks/useMessageHeightTracking";
import {ChatStopReason, MessageWithMetadata} from "../../../types";
import {ChatViewContext} from "./ChatView";
import {
    hasNoAnswer,
    selectActiveThread,
    selectActiveThreadMessages,
    selectIsActiveThreadStreaming,
} from "../threadsSlice";
import {InterruptedTurn} from "../components/error-handling/InterruptedTurn";
import {TruncatedAnswer} from "../components/error-handling/TruncatedAnswer";
import {hasErrorNotice} from "../components/error-handling/errorNotices";
import {ConversationLimitReached} from "../components/error-handling/ConversationLimitReached";
import {TopBarSlot} from "../TopBarContext";
import {ThreadTabs, ThreadTabKey} from "../components/ThreadTabs";
import {PostSearchTabPanel} from "../components/PostSearchTabPanel";

type ThreadProps = {
    id?: string;
    chatDisabled: boolean;
};

const chatInputEstimatedHeight = 7;
const MESSAGE_LIMIT = 50;

export const Thread = ({
    id,
    chatDisabled,
}: ThreadProps) => {
    const userData = bffApi.endpoints.getUser.useQuery();
    const messages = useAppSelector(selectActiveThreadMessages);
    const activeThread = useAppSelector(selectActiveThread);
    const threadName = activeThread?.name;
    const isStreaming = useAppSelector(selectIsActiveThreadStreaming);
    const ephemeralSearch = activeThread?.ephemeralSearch ?? null;

    // Persist tab selection in the URL so back/forward navigation (e.g. after
    // clicking a search result) restores the previously viewed tab.
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const tabFromUrl: ThreadTabKey =
        tabParam === "tools" || tabParam === "sources" ? tabParam : "answer";
    const setActiveTab = useCallback(
        (tab: ThreadTabKey) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (tab === "answer") {
                        next.delete("tab");
                    } else {
                        next.set("tab", tab);
                    }
                    return next;
                },
                {replace: true}
            );
        },
        [setSearchParams]
    );
    const activeTab: ThreadTabKey = tabFromUrl;
    const postSearch = activeThread?.postSearch ?? null;
    const isSearching = postSearch?.status === "searching";
    const toolsCount = postSearch?.tools.length ?? 0;
    const sourcesCount = postSearch?.otherSources.length ?? 0;
    // While searching, show both tabs with loading spinners; once results are
    // ready, show only the lists that have items.
    const showToolsTab =
        isSearching || (postSearch?.status === "ready" && toolsCount > 0);
    const showSourcesTab =
        isSearching || (postSearch?.status === "ready" && sourcesCount > 0);

    // If the active search tab disappears (new turn, error, empty list, etc.)
    // snap back to the answer tab.
    useEffect(() => {
        if (
            (activeTab === "tools" && !showToolsTab) ||
            (activeTab === "sources" && !showSourcesTab)
        ) {
            setActiveTab("answer");
        }
    }, [showToolsTab, showSourcesTab, activeTab]);

    // When the assistant finishes a response while the user is on the search
    // tab, surface a "answer ready" badge on the answer tab. It clears as soon
    // as they switch to the answer tab.
    const [hasAnswerBadge, setHasAnswerBadge] = useState(false);
    const wasStreamingRef = useRef(isStreaming);
    useEffect(() => {
        const wasStreaming = wasStreamingRef.current;
        wasStreamingRef.current = isStreaming;
        if (wasStreaming && !isStreaming && activeTab !== "answer") {
            setHasAnswerBadge(true);
        }
    }, [isStreaming, activeTab]);
    useEffect(() => {
        if (activeTab === "answer" && hasAnswerBadge) {
            setHasAnswerBadge(false);
        }
    }, [activeTab, hasAnswerBadge]);
    const isContentFiltered =
        activeThread?.error?.message?.includes("content_filter") ?? false;
    const threadErrorHasNotice = hasErrorNotice(
        activeThread?.error,
        activeThread?.rateLimitCount ?? 0
    );
    const [animatingPosition, setAnimatingPosition] = useState<number | null>(
        null
    );
    const userMessageCount =
        messages?.filter((m: MessageWithMetadata) => m.role === "user")
            .length ?? 0;
    const {
        scrollRef: viewport,
        isInitial,
        showScrollDownButton,
        forceScrollToBottom,
    } = useChatScroll([messages, animatingPosition], {
        disableAutoScroll: !!ephemeralSearch,
        isStreaming,
    });
    useGutterHover(viewport);
    const last = (messages?.length ?? 0) - 1;
    const lastMsg = messages?.[last];

    // InterruptedTurn reports an answer that finished empty. This notice excludes it.
    const showTruncatedAnswer =
        !isStreaming &&
        activeThread?.stopReason === ChatStopReason.Length &&
        lastMsg?.role === "assistant" &&
        !hasNoAnswer(lastMsg);

    const {containerRef, lastUserMessageRef, heightsMeasured} =
        useMessageHeightTracking(messages, chatInputEstimatedHeight);

    const {threadArtifacts, artifactCardMap} = useThreadArtifacts(messages);

    useEffect(() => {
        if (userMessageCount > 1 && !isStreaming && !ephemeralSearch) {
            viewport.current?.scrollTo(0, viewport.current?.scrollHeight);
        }
    }, [viewport.current, userMessageCount, ephemeralSearch]);

    useEffect(() => {
        if (threadName) {
            document.title = threadName;
        }

        return () => {
            document.title = "KAI";
        };
    }, [threadName]);

    const handleAnimationStart = useCallback((position: number) => {
        setAnimatingPosition(position);
    }, []);

    const handleAnimationFinish = useCallback((position: number) => {
        setAnimatingPosition((current) =>
            current === position ? null : current
        );
    }, []);

    const {sendMessage, resend} = useContext(ChatViewContext);
    const handleSendMessage = useCallback(
        (message: string, attachments: PendingPasteAttachment[]) => {
            const accepted = sendMessage(message, attachments);
            requestAnimationFrame(() => {
                forceScrollToBottom();
            });
            return accepted;
        },
        [sendMessage, forceScrollToBottom]
    );

    return (
        <>
            <TopBarSlot slot="center">
                <ThreadTabs
                    active={activeTab}
                    onChange={setActiveTab}
                    toolsCount={toolsCount}
                    sourcesCount={sourcesCount}
                    searchLoading={postSearch?.status === "searching"}
                    showToolsTab={showToolsTab}
                    showSourcesTab={showSourcesTab}
                    showAnswerBadge={hasAnswerBadge}
                />
            </TopBarSlot>
            <Box
                ref={containerRef}
                className={threadClasses.viewport}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                <div
                    className={threadClasses.panel}
                    data-active={
                        activeTab === "answer" ||
                        (activeTab === "tools" && !showToolsTab) ||
                        (activeTab === "sources" && !showSourcesTab) ||
                        (!showToolsTab && !showSourcesTab)
                    }
                >
                    <Box
                        ref={viewport}
                        className={`${classes.scrollbox} ${classes.gutterReveal} ${
                            !isInitial ? classes.smoothScroll : ""
                        }`}
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            position: "relative",
                            paddingBottom: `${chatInputEstimatedHeight}rem`,
                            scrollPaddingBottom: `${chatInputEstimatedHeight}rem`,
                            marginBottom: `-${chatInputEstimatedHeight + 2}rem`,
                        }}
                        data-name="thread"
                    >
                        <Stack
                            className={threadClasses.threadbox}
                            justify="flex-start"
                            align="stretch"
                        >
                            <ExperimentalEngineIndicator />
                            {messages?.map(
                                (msg: MessageWithMetadata, i: number) => {
                                    if (msg.role === "user") {
                                        return (
                                            <UserMessage
                                                ref={
                                                    i === last - 1
                                                        ? lastUserMessageRef
                                                        : null
                                                }
                                                key={`${id || "thread"}-user-${msg.position}`}
                                                message={msg}
                                                initials={
                                                    userData.data?.initials ??
                                                    "U"
                                                }
                                                forceShowEdit={
                                                    isContentFiltered &&
                                                    i === last - 1
                                                }
                                            />
                                        );
                                    }
                                    return (
                                        <div
                                            key={`${id || "thread"}-assistant-${msg.position}`}
                                        >
                                            <AssistantMessage
                                                message={msg}
                                                isStreaming={
                                                    isStreaming && i === last
                                                }
                                                onAnimationStart={
                                                    handleAnimationStart
                                                }
                                                onAnimationFinish={
                                                    handleAnimationFinish
                                                }
                                                isLastMessageInThread={
                                                    i === last
                                                }
                                                heightsMeasured={
                                                    i === last
                                                        ? heightsMeasured
                                                        : undefined
                                                }
                                                threadArtifacts={
                                                    threadArtifacts
                                                }
                                                artifactCards={artifactCardMap.get(
                                                    msg.position
                                                )}
                                            />
                                            {i === last &&
                                                !isStreaming &&
                                                !!msg.followUpSuggestions
                                                    ?.length && (
                                                    <FollowUpSuggestionChips
                                                        suggestions={
                                                            msg.followUpSuggestions
                                                        }
                                                        onSelect={(question) =>
                                                            handleSendMessage(
                                                                question,
                                                                []
                                                            )
                                                        }
                                                    />
                                                )}
                                        </div>
                                    );
                                }
                            )}
                            {showTruncatedAnswer && <TruncatedAnswer />}
                            {(() => {
                                // The turn gave the user no answer. Either the
                                // turn made no assistant message, or it made
                                // one that finished empty. This block stays
                                // hidden only when the thread error has its own
                                // notice in ErrorReason, which is more exact.
                                // ErrorReason shows nothing for some error
                                // types, and then this notice is the only one
                                // the user gets.
                                if (
                                    !lastMsg ||
                                    isStreaming ||
                                    threadErrorHasNotice
                                ) {
                                    return null;
                                }
                                // Find the question that got no answer. It is
                                // the last message, unless an empty assistant
                                // message comes after it.
                                const emptyAnswer =
                                    lastMsg.role === "assistant" &&
                                    hasNoAnswer(lastMsg);
                                const retryMsg = emptyAnswer
                                    ? messages?.[last - 1]
                                    : lastMsg;
                                if (retryMsg?.role !== "user") {
                                    return null;
                                }
                                return (
                                    <InterruptedTurn
                                        onRetry={() =>
                                            resend(
                                                retryMsg.content,
                                                retryMsg.position
                                            )
                                        }
                                    />
                                );
                            })()}
                        </Stack>
                    </Box>
                    {(messages?.length ?? 0) >= MESSAGE_LIMIT ? (
                        <ConversationLimitReached />
                    ) : (
                        <ChatInput
                            disabled={chatDisabled}
                            onSubmit={handleSendMessage}
                            scrollRef={viewport}
                            showScrollDownButton={showScrollDownButton}
                        />
                    )}
                </div>
                {showToolsTab && (
                    <div
                        className={threadClasses.panel}
                        data-active={activeTab === "tools"}
                    >
                        <PostSearchTabPanel state={postSearch} kind="tools" />
                    </div>
                )}
                {showSourcesTab && (
                    <div
                        className={threadClasses.panel}
                        data-active={activeTab === "sources"}
                    >
                        <PostSearchTabPanel state={postSearch} kind="sources" />
                    </div>
                )}
            </Box>
        </>
    );
};
